import { ScanFinding } from '../types/scan.ts';

/**
 * Groups findings that are identical (same file_path + line + issue_type)
 * Returns an array of grouped findings with occurrence counts
 */
export interface GroupedFinding {
  key: string;
  primaryFinding: ScanFinding;
  occurrences: ScanFinding[];
  count: number;
  file_path: string;
  line: number;
  issue_type: string;
  severity: string;
}

/**
 * Create a unique key for deduplication based on file_path + line + issue_type
 */
export const createDeduplicationKey = (finding: ScanFinding): string => {
  return `${finding.file_path}|${finding.line}|${finding.issue_type}`;
};

/**
 * Deduplicate findings by grouping identical ones (same file_path + line + issue_type)
 * @param findings Array of scan findings
 * @returns Array of grouped findings with occurrence counts
 */
export const deduplicateFindings = (findings: ScanFinding[]): GroupedFinding[] => {
  const grouped = new Map<string, ScanFinding[]>();

  // Group findings by the deduplication key
  findings.forEach(finding => {
    const key = createDeduplicationKey(finding);
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(finding);
  });

  // Convert to GroupedFinding format
  return Array.from(grouped.entries()).map(([key, occurrences]) => {
    const primaryFinding = occurrences[0]; // Use first occurrence as primary
    return {
      key,
      primaryFinding,
      occurrences,
      count: occurrences.length,
      file_path: primaryFinding.file_path,
      line: primaryFinding.line,
      issue_type: primaryFinding.issue_type,
      severity: primaryFinding.severity,
    };
  }).sort((a, b) => {
    // Sort by severity first (critical -> high -> medium -> low -> info)
    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
    const aSeverity = severityOrder[a.severity as keyof typeof severityOrder] ?? 4;
    const bSeverity = severityOrder[b.severity as keyof typeof severityOrder] ?? 4;
    
    if (aSeverity !== bSeverity) {
      return aSeverity - bSeverity;
    }
    
    // Then by occurrence count (most duplicates first)
    if (a.count !== b.count) {
      return b.count - a.count;
    }
    
    // Finally by file path alphabetically
    return a.file_path.localeCompare(b.file_path);
  });
};

/**
 * Convert grouped findings back to individual findings for compatibility
 * @param groupedFindings Array of grouped findings
 * @param hideDuplicates Whether to show only one instance of each group
 * @returns Array of individual scan findings
 */
export const expandGroupedFindings = (
  groupedFindings: GroupedFinding[], 
  hideDuplicates: boolean = false
): ScanFinding[] => {
  if (hideDuplicates) {
    // Return only the primary finding from each group
    return groupedFindings.map(group => group.primaryFinding);
  } else {
    // Return all occurrences
    return groupedFindings.flatMap(group => group.occurrences);
  }
};

/**
 * Get statistics about deduplication
 */
export const getDeduplicationStats = (findings: ScanFinding[]) => {
  const grouped = deduplicateFindings(findings);
  const totalFindings = findings.length;
  const uniqueFindings = grouped.length;
  const duplicateGroups = grouped.filter(g => g.count > 1).length;
  const duplicatesRemoved = totalFindings - uniqueFindings;

  return {
    totalFindings,
    uniqueFindings,
    duplicateGroups,
    duplicatesRemoved,
    duplicatePercentage: totalFindings > 0 ? Math.round((duplicatesRemoved / totalFindings) * 100) : 0,
  };
}; 